import {
  type EquipmentTotals,
  NO_ARTIFACT_EFFECTS,
  NO_EQUIPMENT_TOTALS,
  type ScaledArtifactEffects,
} from '@kenocyber/shared';
import { DAMAGE_MILLI_PER_UNIT, shipBaseDamageMilli } from '../damage/base';
import { HEALTH_MILLI_PER_UNIT } from '../hull/max-health';
import { effectiveBaseDamage } from './attributes';

/**
 * THE authoritative order in which passives become attributes.
 *
 * One place, deliberately. Every consumer - the simulation, the Overview, the
 * server's re-simulation - reads these functions, so there is no second
 * arithmetic anywhere that could disagree about what a player's damage is.
 *
 * The order, and why:
 *
 * 1. **Flat base increases first.** Artifact `baseHealth` and
 *    `baseAttackSpeedMicro`, and the equipment's flat health, base attack and
 *    base attack speed, are added to the base the allocated points produced,
 *    BEFORE any percentage. Adding them afterwards would make a percentage
 *    bonus quietly worth less on a ship carrying Mother Ship.
 * 2. **Percentages are summed, then applied once.** Damage from the ship, from
 *    Guns, from Double Kill, from Yeeeaaah and from every equipped component
 *    add up to a single multiplier - they are additive with each other, never
 *    multiplicative, so the value is the same however the sources are ordered.
 * 3. **Totals come after bases.** `total = base * (1 + sumOfPercentages)`.
 * 4. **The BONUS ATTACK lands last, outside the multiplier.** It is the one
 *    number that is not part of the main formula: `damage = principal + bonus`,
 *    per projectile, never multiplied by the damage percentage and never folded
 *    into the base. That is what makes it worth the same to a beginner and to a
 *    player carrying +900%, and it is why the Overview shows it on its own line.
 * 5. **Gold: flat per enemy first, then the percentage on top.** Both apply to
 *    normal mobs and to the boss, with no separate rule for either.
 *
 * Percentages are basis points and stay integers until the final division:
 * 10 000 bps = 100%. Attack speed arrives in micro-attacks per second, so
 * 0.06/s is the integer 60 000 and only becomes a float at the very end, when
 * the physics needs one. Equipment arrives in milli-damage, milli-health,
 * milli-Gold and micro-attacks for exactly the same reason.
 */

/** 10 000 basis points = 100%. */
export const BPS_PER_UNIT = 10_000n;
/** 1 000 000 micro-attacks per second = 1 attack per second. */
export const ASPD_MICRO_PER_UNIT = 1_000_000;
/**
 * 1 000 milli-health = 1 health point.
 *
 * Re-exportada de `hull/max-health.ts` e não redeclarada: é a unidade em que a
 * composição canónica da vida máxima faz a sua aritmética inteira, e duas
 * constantes com o mesmo nome e o mesmo valor em dois módulos são duas
 * constantes que um dia divergem.
 */
export { HEALTH_MILLI_PER_UNIT };
/** 1 000 milli-Gold = 1 Gold. */
export const GOLD_MILLI_PER_UNIT = 1000n;

/** Everything a hunt needs to know about the account's artifacts. */
export interface PassiveAttributes {
  readonly effects: ScaledArtifactEffects;
}

export const NO_PASSIVES: PassiveAttributes = Object.freeze({ effects: NO_ARTIFACT_EFFECTS });

/** Step 1: flat base health added before any percentage. */
export function passiveBaseHealth(passives: PassiveAttributes): number {
  return Number(passives.effects.baseHealth);
}

/**
 * Step 1: flat base attack speed, in attacks per second.
 *
 * Deliberately NOT capped, smoothed or divided down. Speed Fast at level 10 is
 * 30.72 attacks per second on its own, and the game shows exactly that - as
 * more discrete projectiles, never as a beam (see `CLAUDE.md`).
 */
export function passiveBaseAttackSpeed(passives: PassiveAttributes): number {
  return Number(passives.effects.baseAttackSpeedMicro) / ASPD_MICRO_PER_UNIT;
}

/** Step 2: artifact damage percentage, as a percentage (not basis points). */
export function passiveDamagePercent(passives: PassiveAttributes): number {
  return Number(passives.effects.damageBps) / 100;
}

/** Step 2: artifact maximum-health percentage. */
export function passiveMaxHealthPercent(passives: PassiveAttributes): number {
  return Number(passives.effects.maxHealthBps) / 100;
}

/**
 * Step 2, EXACTLY: the artifacts' maximum-health percentage in basis points.
 *
 * The `bigint` beside {@link passiveMaxHealthPercent}, and the one the hull's
 * composition actually consumes. The percentage above is for a panel to print;
 * this is the parcel that is summed with the equipment's and with a jewel's and
 * applied once, in integers, by `hull/max-health.ts`.
 *
 * Two readings of one number, and only one of them decides a hull - which is
 * why the float one no longer feeds the arithmetic (gate V28G.R).
 */
export function passiveMaxHealthBps(passives: PassiveAttributes): bigint {
  return passives.effects.maxHealthBps;
}

/** Health regenerated per second, in whole health units. */
export function passiveRegenPerSecond(passives: PassiveAttributes): number {
  return Number(passives.effects.regenMilliPerSecond) / HEALTH_MILLI_PER_UNIT;
}

/* -------------------------------------------------------------------------- */
/*  Equipment                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * The five equipped components, as the same four steps.
 *
 * They enter the SAME order as the artifacts and are additive with them: a
 * component's +5% damage and an artifact's +5% damage are +10%, never +10,25%.
 * The only member of the family that behaves differently is the bonus attack,
 * and it is different by design rather than by omission - see step 4 above.
 */

/**
 * **Step 1, as the one authoritative base damage.**
 *
 * `SHIP_BASE_DAMAGE_MILLI` for the hunt: the allocated points' base plus the
 * components' flat base attack, in exact milli-damage, before every percentage
 * and before the bonus attack.
 *
 * This replaced `equipmentBaseAttack`, which returned `baseAttackMilli / 1000`
 * as a float for the simulation to add to `effectiveBaseDamage`. Two additions
 * in floating point where one integer sum will do, and - worse - a second place
 * that knew what "base damage" meant. `docs/gates/v28d2-jewel-mechanics-contract.md`
 * asks for exactly one, because every jewel coefficient multiplies it.
 *
 * The scaling and its bounds live in `damage/base.ts`, which knows nothing
 * about the hunt. This is the adapter that knows where the two numbers come
 * from, and it is one line.
 */
export function huntShipBaseDamageMilli(damagePoints: bigint, totals: EquipmentTotals): number {
  return shipBaseDamageMilli(effectiveBaseDamage(damagePoints), totals.baseAttackMilli);
}

/**
 * Step 4: the bonus attack, in damage points.
 *
 * Applied to EACH projectile, after the main formula, and never multiplied by
 * anything. A volley of twenty-eight shots therefore carries it twenty-eight
 * times - once per projectile, exactly as the main damage does - because every
 * shot is its own hit with its own number.
 */
export function equipmentBonusAttack(totals: EquipmentTotals): number {
  return Number(totals.bonusAttackMilli) / DAMAGE_MILLI_PER_UNIT;
}

/** Step 2: the components' damage percentage, as a percentage. */
export function equipmentDamagePercent(totals: EquipmentTotals): number {
  return Number(totals.attackBps) / 100;
}

/** Step 1: flat base attack speed, in attacks per second. */
export function equipmentBaseAttackSpeed(totals: EquipmentTotals): number {
  return Number(totals.baseAttackSpeedMicro) / ASPD_MICRO_PER_UNIT;
}

/** Step 2: the components' attack-speed percentage, as a percentage. */
export function equipmentAttackSpeedPercent(totals: EquipmentTotals): number {
  return Number(totals.attackSpeedBps) / 100;
}

/** Step 1: flat maximum health, in health points. */
export function equipmentBaseHealth(totals: EquipmentTotals): number {
  return Number(totals.maxHealthMilli) / HEALTH_MILLI_PER_UNIT;
}

/** Step 2: the components' maximum-health percentage, as a percentage. */
export function equipmentMaxHealthPercent(totals: EquipmentTotals): number {
  return Number(totals.maxHealthBps) / 100;
}

/** Step 2, EXACTLY: the components' maximum-health percentage in basis points. */
export function equipmentMaxHealthBps(totals: EquipmentTotals): bigint {
  return totals.maxHealthBps;
}

/* -------------------------------------------------------------------------- */
/*  Gold                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Step 5: what `kills` enemies are worth once artifacts and equipment apply.
 *
 * Takes the base reward the balance already decided and adds the flat Gold per
 * enemy, then the percentage - in that order, in integers, on a CUMULATIVE
 * total rather than per kill.
 *
 * Cumulative is the load-bearing part. The base reward is already a fraction
 * (one Gold per two kills), the equipment's flat Gold is a fraction (thousandths
 * of a Gold per enemy), and the percentage makes another one; computing per kill
 * and rounding would silently destroy all three. Taking the difference between
 * two calls to this function is what turns the exact rate into whole Gold, and
 * it keeps working across a checkpoint, an F5, a death, a combine, an equip and
 * a reconnection because the counter it reads is authoritative state.
 *
 * The whole calculation runs in MILLI-Gold and floors ONCE at the end. An
 * account with no equipment gets byte-identical answers to the version that
 * knew nothing about it - proved by a test, because a silent change here would
 * be a silent change to every payout in the game.
 *
 * @param baseTotal cumulative Gold owed for those kills before any bonus
 * @param kills     cumulative confirmed kills of that kind
 */
export function goldWithPassives(
  baseTotal: bigint,
  kills: bigint,
  passives: PassiveAttributes,
  equipment: EquipmentTotals = NO_EQUIPMENT_TOTALS,
): bigint {
  // Flat first, in thousandths: every enemy is worth this much more, exactly.
  const flatPerEnemyMilli =
    passives.effects.goldPerKill * GOLD_MILLI_PER_UNIT + equipment.goldPerEnemyMilli;
  const withFlatMilli = baseTotal * GOLD_MILLI_PER_UNIT + kills * flatPerEnemyMilli;
  // Then the SUMMED percentage, once, on the flat-inclusive subtotal.
  const percentBps = passives.effects.goldBps + equipment.goldBps;
  const withPercentMilli = withFlatMilli + (withFlatMilli * percentBps) / BPS_PER_UNIT;
  // One floor, at the very end. The remainder is not lost: the next kill's
  // cumulative total carries it, because this is recomputed from the running
  // counter every time.
  return withPercentMilli / GOLD_MILLI_PER_UNIT;
}
